<template>
  <v-app>
    <!-- Left Sidebar -->
    <v-navigation-drawer
      v-model="drawer"
      :rail="rail"
      permanent
      color="#1a1a2e"
      class="sidebar"
    >
      <!-- Logo Area -->
      <div class="sidebar-logo" @click="rail = !rail">
        <img
          v-if="!rail"
          src="/images/classmom_logo.png"
          alt="ClassMom"
          class="logo-img"
        />
        <v-icon v-else color="white" size="28">mdi-school</v-icon>
      </div>

      <v-divider color="rgba(255,255,255,0.12)" />

      <!-- User Profile Section -->
      <div class="sidebar-user-top" :class="{ 'sidebar-user-top--rail': rail }">
        <v-avatar
          color="#667de8"
          size="34"
          class="user-avatar flex-shrink-0"
          style="cursor:pointer"
          @click="!rail && $router.push('/dashboard/profile')"
        >
          <span class="text-body-2 font-weight-bold text-white">{{ userInitials }}</span>
        </v-avatar>
        <div v-if="!rail" class="user-info ml-2 flex-grow-1 overflow-hidden">
          <div class="sidebar-user-name text-truncate">{{ userName }}</div>
          <div class="sidebar-user-role text-truncate">{{ userRole }}</div>
        </div>
        <v-btn
          v-if="!rail"
          icon="mdi-account-circle-outline"
          variant="text"
          size="small"
          color="rgba(255,255,255,0.55)"
          to="/dashboard/profile"
          class="ml-1 flex-shrink-0"
        />
      </div>

      <v-divider color="rgba(255,255,255,0.12)" />

      <!-- Top-level Navigation Items -->
      <v-list nav density="comfortable" class="mt-2">
        <v-list-item
          v-for="item in navItems"
          :key="item.name"
          :to="item.to"
          :title="item.name"
          active-color="white"
          base-color="rgba(255,255,255,0.7)"
          rounded="lg"
          class="nav-item mb-1"
        >
          <template #prepend>
            <v-badge
              v-if="item.badge && rail"
              :content="item.badge"
              color="error"
              floating
            >
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>
            <v-icon v-else>{{ item.icon }}</v-icon>
          </template>
          <template v-if="item.badge && !rail" #append>
            <v-badge :content="item.badge" color="error" inline />
          </template>
        </v-list-item>
      </v-list>

      <!-- Context Navigation (school/agent sections) -->
      <div v-if="contextNav.items.length" class="context-nav-panel" :class="{ 'context-nav-panel--rail': rail }">
        <!-- Section label -->
        <div v-if="!rail && contextNav.title" class="context-nav-label--light">
          {{ contextNav.title }}
        </div>
        <div v-else-if="rail" class="context-nav-dot--light" title="Section navigation" />

        <v-list nav density="comfortable" class="mt-1 pa-2" bg-color="transparent">
          <v-list-item
            v-for="item in contextNav.items"
            :key="item.value"
            :prepend-icon="item.icon"
            :title="item.title"
            :active="contextNav.active === item.value"
            active-color="#1a1a2e"
            base-color="#555"
            rounded="lg"
            class="nav-item context-nav-item mb-1"
            @click="contextNav.active = item.value"
          >
            <template v-if="item.badge && !rail" #append>
              <v-badge :content="item.badge" color="error" inline />
            </template>
          </v-list-item>
        </v-list>
      </div>

      <template #append>
        <v-divider color="rgba(255,255,255,0.12)" class="mb-2" />
        <v-list nav density="comfortable" class="pb-2">
          <v-list-item
            prepend-icon="mdi-logout"
            title="Logout"
            base-color="rgba(255,255,255,0.6)"
            rounded="lg"
            class="nav-item"
            @click="handleLogout"
          />
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Top App Bar -->
    <v-app-bar flat color="white" border="b" :height="schoolLogo ? 168 : 64">
      <v-btn
        :icon="rail ? 'mdi-menu' : 'mdi-menu-open'"
        variant="text"
        @click="rail = !rail"
      />
      <v-app-bar-title>
        <div class="appbar-title-area">
          <img
            v-if="schoolLogo"
            :src="schoolLogo"
            :alt="schoolName"
            class="appbar-school-logo"
          />
          <div class="appbar-text">
            <div v-if="schoolDisplay" class="appbar-school-name">{{ schoolDisplay }}</div>
            <div class="appbar-page-title" :class="{ 'appbar-page-title--solo': !schoolDisplay }">{{ pageTitle }}</div>
          </div>
        </div>
      </v-app-bar-title>
      <template #append>
        <div class="pr-4 text-body-2 text-medium-emphasis">{{ currentDate }}</div>
      </template>
    </v-app-bar>

    <!-- Main Content -->
    <v-main>
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { ref, computed, reactive, provide, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { clearAuth } from '@/utils/auth'
import axios from 'axios'

let inboxPollInterval = null

export default {
  name: 'MainLayout',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const drawer = ref(true)
    const rail = ref(false)
    const userDataRaw = ref(null)
    const inboxUnread = ref(0)

    const readUserData = () => {
      try { userDataRaw.value = JSON.parse(localStorage.getItem('user') || '{}') }
      catch { userDataRaw.value = {} }
    }
    readUserData()

    const pollInboxUnread = async () => {
      const u = userDataRaw.value || {}
      if (u.role !== 'contributor') return
      const schoolId = u.school_id || u.school?.id
      if (!schoolId) return
      try {
        const { data } = await axios.get(`/api/schools/${schoolId}/staff-notifications`, {
          params: { limit: 1, offset: 0, unread_only: true }
        })
        inboxUnread.value = data.unreadCount || 0
      } catch { /* ignore */ }
    }

    const onInboxUnread = (e) => { inboxUnread.value = e.detail || 0 }

    onMounted(() => {
      window.addEventListener('classmom:user-updated', readUserData)
      window.addEventListener('classmom:inbox-unread', onInboxUnread)
      pollInboxUnread()
      inboxPollInterval = setInterval(pollInboxUnread, 60000)
    })
    onUnmounted(() => {
      window.removeEventListener('classmom:user-updated', readUserData)
      window.removeEventListener('classmom:inbox-unread', onInboxUnread)
      clearInterval(inboxPollInterval)
    })

    const userData = computed(() => userDataRaw.value || {})

    // Context nav — populated by child views (SchoolDetailView, AgentDetailView)
    const contextNav = reactive({
      items: [],    // [{ value, title, icon, badge? }]
      active: null, // currently selected value
      title: '',    // section group label (e.g. school name or agent name)
    })
    provide('contextNav', contextNav)

    const navItems = computed(() => {
      const items = [
        { name: 'Dashboard', icon: 'mdi-view-dashboard-outline', to: '/dashboard' },
      ]
      const { role, school_id, school } = userData.value
      const resolvedSchoolId = school_id || school?.id
      if (role === 'super_admin') {
        items.push({ name: 'Schools',            icon: 'mdi-domain',                        to: '/dashboard/schools' })
        items.push({ name: 'Broadcasts',         icon: 'mdi-broadcast',                     to: '/dashboard/broadcasts' })
        items.push({ name: 'WA Simulator',       icon: 'mdi-whatsapp',                      to: '/dashboard/wa-simulator' })
        items.push({ name: 'Knowledge Base',     icon: 'mdi-file-document-multiple-outline', to: '/dashboard/knowledge' })
        items.push({ name: 'School Prospecting', icon: 'mdi-map-search-outline',            to: '/dashboard/prospecting' })

      } else if (role === 'administrator' && resolvedSchoolId) {
        items.push({ name: 'My School',      icon: 'mdi-office-building-outline', to: `/dashboard/schools/${resolvedSchoolId}` })
        items.push({ name: 'Agents',         icon: 'mdi-robot-outline',            to: '/dashboard/agents' })
        items.push({ name: 'Conversations',  icon: 'mdi-chat-outline',             to: '/dashboard/conversations' })
        items.push({ name: 'Broadcasts',     icon: 'mdi-broadcast',                to: '/dashboard/broadcasts' })
        items.push({ name: 'Knowledge Base', icon: 'mdi-file-document-multiple-outline', to: `/dashboard/knowledge` })
      } else if (role === 'contributor' && resolvedSchoolId) {
        items.push({ name: 'Inbox',          icon: 'mdi-bell-outline',  to: '/dashboard/inbox',          badge: inboxUnread.value || null })
        items.push({ name: 'Agents',         icon: 'mdi-robot-outline', to: '/dashboard/agents' })
        items.push({ name: 'Conversations',  icon: 'mdi-chat-outline',  to: '/dashboard/conversations' })
        items.push({ name: 'Broadcasts',     icon: 'mdi-broadcast',     to: '/dashboard/broadcasts' })
        items.push({ name: 'Knowledge Base', icon: 'mdi-file-document-multiple-outline', to: '/dashboard/knowledge' })
      }
      return items
    })

    const schoolLogo    = computed(() => userData.value.school?.logo_url || null)
    const schoolName    = computed(() => userData.value.school?.name || 'School')
    const schoolDisplay = computed(() => userData.value.school?.name || null)

    const userName = computed(() => {
      const u = userData.value
      if (u.first_name && u.last_name) return `${u.first_name} ${u.last_name}`
      if (u.first_name) return u.first_name
      return u.displayName || u.email || 'User'
    })

    const userRole = computed(() => {
      const roles = {
        administrator: 'Administrator',
        contributor: 'Teacher',
        parent: 'Parent',
        student: 'Student',
        super_admin: 'Super Admin'
      }
      return roles[userData.value.role] || 'User'
    })

    const userInitials = computed(() => {
      const name = userName.value
      return name
        .split(' ')
        .map(w => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    })

    // Page title: prefer active context nav item label, fall back to route meta
    const pageTitle = computed(() => {
      if (contextNav.active && contextNav.items.length) {
        const item = contextNav.items.find(i => i.value === contextNav.active)
        if (item) return item.title
      }
      return route.meta.title?.replace(' - ClassMom', '') || 'Admin Dashboard'
    })

    const currentDate = computed(() => {
      return new Date().toLocaleDateString('en-ZA', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      })
    })

    const handleLogout = async () => {
      try {
        await axios.post('/api/auth/logout')
      } catch { /* ignore server errors on logout */ }
      clearAuth()
      router.push({ name: 'login' })
    }

    return {
      drawer,
      rail,
      navItems,
      contextNav,
      schoolLogo,
      schoolName,
      schoolDisplay,
      userName,
      userRole,
      userInitials,
      pageTitle,
      currentDate,
      handleLogout,
      inboxUnread,
    }
  }
}
</script>

<style scoped>
.sidebar {
  border-right: none !important;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  cursor: pointer;
  min-height: 72px;
}

.logo-img {
  height: 36px;
  width: auto;
  filter: brightness(0) invert(1);
  transition: all 0.2s ease;
}

.nav-item {
  transition: background-color 0.2s ease;
}

.context-nav-item {
  font-size: 0.85rem;
}

.context-nav-panel {
  margin: 8px 8px 4px;
  background: #e8eaed;
  border-radius: 12px;
  overflow: hidden;
}

.context-nav-panel--rail {
  margin: 8px 6px 4px;
}

.context-nav-label--light {
  padding: 10px 14px 2px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.context-nav-dot--light {
  display: flex;
  justify-content: center;
  padding: 6px 0 2px;
}

/* White text on main (dark) sidebar nav items */
.sidebar :deep(.v-list-item-title),
.sidebar :deep(.v-list-item__content),
.sidebar :deep(.v-icon) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.sidebar :deep(.v-list-item--active .v-list-item-title),
.sidebar :deep(.v-list-item--active .v-icon) {
  color: #ffffff !important;
}

/* Override: dark text inside the light context nav panel */
.context-nav-panel :deep(.v-list-item-title),
.context-nav-panel :deep(.v-icon) {
  color: #555 !important;
}

.context-nav-panel :deep(.v-list-item--active .v-list-item-title),
.context-nav-panel :deep(.v-list-item--active .v-icon) {
  color: #1a1a2e !important;
}

.context-nav-panel :deep(.v-list-item--active) {
  background: rgba(26, 26, 46, 0.1) !important;
}

.sidebar :deep(.context-nav-item .v-list-item-title) {
  font-size: 0.85rem;
}

.sidebar-user-top {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  min-height: 60px;
}

.sidebar-user-top--rail {
  justify-content: center;
  padding: 12px 0;
}

.user-avatar {
  flex-shrink: 0;
}

.user-info {
  overflow: hidden;
}

.sidebar-user-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-role {
  font-size: 0.68rem;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.appbar-title-area {
  display: flex;
  align-items: center;
  gap: 14px;
}

.appbar-school-logo {
  max-height: 152px;
  max-width: 152px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;
}

.appbar-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.appbar-school-name {
  font-weight: 700;
  font-size: 1.05rem;
  color: #1a1a2e;
  line-height: 1.25;
}

.appbar-page-title {
  font-weight: 400;
  font-size: 0.8rem;
  color: #888;
  line-height: 1.25;
}

.appbar-page-title--solo {
  font-weight: 600;
  font-size: 0.95rem;
  color: #1a1a2e;
}
</style>
